import React, { useState, useEffect, useRef } from 'react'
import Button from '../../../components/ui/Button'
import Icon from '../../../components/AppIcon'
import { db } from '../../../firebase'
import { collection, query, getDocs, orderBy, limit } from 'firebase/firestore'

const MAX_CUSTOMERS_FETCH = 500
const MAX_RESULTS = 10
const SEARCH_DEBOUNCE_MS = 300

const INPUT_CLASSES =
  'w-full px-3 py-2.5 border border-border/60 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20'

const CustomerSearch = ({ selected, onSelect, showNewCustomer = true }) => {
  const [search, setSearch] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    idNumber: ''
  })
  // One fetch per mount; filtering happens client-side over the full list
  // (the shop has <100 customers — fetching page 1 alphabetically and
  // filtering only that page missed almost everyone).
  const allCustomersRef = useRef(null)

  useEffect(() => {
    if (search.length < 2) {
      setResults([])
      return
    }

    let cancelled = false

    const searchCustomers = async () => {
      setLoading(true)
      try {
        if (!allCustomersRef.current) {
          const q = query(
            collection(db, 'gold_customers'),
            orderBy('name'),
            limit(MAX_CUSTOMERS_FETCH)
          )
          const snap = await getDocs(q)
          allCustomersRef.current = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        }

        const term = search.toLowerCase()
        const matches = allCustomersRef.current
          .filter(c =>
            c.name?.toLowerCase().includes(term) ||
            c.nameLower?.includes(term) ||
            c.phone?.includes(search) ||
            c.email?.toLowerCase().includes(term)
          )
          .slice(0, MAX_RESULTS)

        if (!cancelled) setResults(matches)
      } catch (err) {
        console.error('Error searching customers:', err)
        if (!cancelled) setResults([])
      }
      if (!cancelled) setLoading(false)
    }

    const debounce = setTimeout(searchCustomers, SEARCH_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(debounce)
    }
  }, [search])

  const handleSelectCustomer = (customer) => {
    onSelect(customer)
    setSearch('')
    setResults([])
  }

  const handleClearCustomer = () => {
    onSelect(null)
  }

  const handleCreateCustomer = () => {
    const trimmed = {
      name: newCustomer.name.trim(),
      email: newCustomer.email.trim(),
      phone: newCustomer.phone.trim(),
      idNumber: newCustomer.idNumber.trim()
    }
    if (trimmed.email && !/\S+@\S+\.\S+/.test(trimmed.email)) return
    const customer = {
      id: `new_${Date.now()}`,
      ...trimmed,
      createdAt: new Date().toISOString()
    }
    onSelect(customer)
    setShowNew(false)
    setNewCustomer({ name: '', email: '', phone: '', idNumber: '' })
  }

  if (selected) {
    return (
      <div className="flex items-center justify-between p-3 bg-primary/5 border border-primary/20 rounded-xl">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-primary/10 border border-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="User" size={20} className="text-primary" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-foreground truncate">{selected.name}</p>
            <p className="text-sm text-muted-foreground truncate">{selected.phone || selected.email}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClearCustomer}
          aria-label="Clear selected customer"
          className="text-muted-foreground hover:text-foreground flex-shrink-0"
        >
          <Icon name="X" size={18} />
        </Button>
      </div>
    )
  }

  if (showNew) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="font-medium text-foreground">New Customer</span>
          <Button variant="ghost" size="icon" onClick={() => setShowNew(false)} aria-label="Close new customer form">
            <Icon name="X" size={16} />
          </Button>
        </div>
        <input
          type="text"
          placeholder="Full Name *"
          maxLength={256}
          value={newCustomer.name}
          onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
          className={INPUT_CLASSES}
        />
        <input
          type="tel"
          placeholder="Phone"
          maxLength={20}
          value={newCustomer.phone}
          onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
          className={INPUT_CLASSES}
        />
        <input
          type="email"
          placeholder="Email"
          maxLength={256}
          value={newCustomer.email}
          onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
          className={INPUT_CLASSES}
        />
        <input
          type="text"
          placeholder="ID Number"
          maxLength={50}
          value={newCustomer.idNumber}
          onChange={(e) => setNewCustomer({ ...newCustomer, idNumber: e.target.value })}
          className={INPUT_CLASSES}
        />
        <Button
          variant="default"
          className="w-full"
          onClick={handleCreateCustomer}
          disabled={!newCustomer.name.trim()}
        >
          Add Customer
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search by name, phone, or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 border border-border/60 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Search Results */}
      {results.length > 0 && (
        <div className="border border-border/60 rounded-lg divide-y divide-border/60 max-h-48 overflow-y-auto">
          {results.map(customer => (
            <button
              key={customer.id}
              onClick={() => handleSelectCustomer(customer)}
              className="w-full flex items-center gap-3 p-3 min-h-[3rem] hover:bg-muted/30 transition-colors text-left"
            >
              <div className="w-8 h-8 bg-muted/50 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="User" size={16} className="text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground truncate">{customer.name}</p>
                <p className="text-xs text-muted-foreground truncate">{customer.phone || customer.email}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results */}
      {search.length >= 2 && results.length === 0 && !loading && (
        <p className="text-sm text-muted-foreground text-center py-2">No customers found</p>
      )}

      {/* New Customer Button */}
      {showNewCustomer && (
        <Button
          variant="outline"
          className="w-full"
          onClick={() => setShowNew(true)}
          iconName="UserPlus"
          iconPosition="left"
        >
          New Customer
        </Button>
      )}
    </div>
  )
}

export default CustomerSearch
